import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FaIcons from 'react-icons/fa';
import './TracksSection.css';

// Create icon components with proper typing
const FaRobot = FaIcons.FaRobot as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaMicrochip = FaIcons.FaMicrochip as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaBrain = FaIcons.FaBrain as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaHelicopter = FaIcons.FaHelicopter as React.ComponentType<React.SVGProps<SVGSVGElement>>; // Using helicopter icon instead of drone
const FaPlane = FaIcons.FaPlane as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaCode = FaIcons.FaCode as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaTools = FaIcons.FaTools as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaChartLine = FaIcons.FaChartLine as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const TracksSection: React.FC = () => {
  const [headerRef, headerInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [track1Ref, track1InView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [track2Ref, track2InView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const skillVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <section className="tracks-section" id="tracks">
      <div className="container">
        <motion.div
          className="tracks-header"
          ref={headerRef}
          initial="hidden"
          animate={headerInView ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <div className="tracks-header">
            <h2 className="section-title text-center" style={{
              background: 'linear-gradient(135deg, #6366f1, #10b981)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>
              Program Structure & Tracks
            </h2>
            <p className="section-subtitle" style={{
              color: '#000000',
              WebkitTextFillColor: '#000000',
              '--text-color': '#000000',
              '--text-fill-color': '#000000'
            } as React.CSSProperties}>
              Choose the perfect track for your child's interests and watch them thrive in our carefully designed, hands-on learning environments.
            </p>
          </div>
        </motion.div>

        <div className="tracks-container">
          <motion.div
            className="track-card"
            id="robotics"
            ref={track1Ref}
            initial="hidden"
            animate={track1InView ? "visible" : "hidden"}
            variants={fadeIn}
          >
            <div className="track-content">
              <div className="track-header">
                <span className="track-number">Track 1</span>
                <h3 className="track-title">Robotics + IoT + Artificial Intelligence</h3>
                <p className="track-tagline">Build, Program, Innovate</p>
              </div>

              <div className="track-main-content">
                <div className="track-info">
                  <ul className="track-features">
                    <li>Basics of Electronics, Sensors, and Actuators</li>
                    <li>Build Real Bots with Arduino</li>
                    <li>Learn Robotics and Automation</li>
                    <li>Hands-on IoT Models using ESP32 Microcontroller</li>
                    <li>IoT and Automation Voice Control Projects</li>
                    <li>Build Your First AI Model</li>
                    <li>Explore AI Tools for Real-World Application</li>
                  </ul>
                </div>

                <motion.div 
                  className="track-image-container"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img src="/images/gallery/Robotics.jpg" alt="Students working on robotics projects" />
                  <div className="image-overlay">
                    <span>Hands-on Learning Experience</span>
                  </div>
                </motion.div>
              </div>

              <motion.div
                className="track-skills"
                variants={staggerContainer}
              >
                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaTools /></div>
                  <h4>Circuit Design</h4>
                  <p>Learn to design and build electronic circuits</p>
                </motion.div>

                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaCode /></div>
                  <h4>Coding Skills</h4>
                  <p>Develop programming skills for robots and AI</p>
                </motion.div>

                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaRobot /></div>
                  <h4>Hands-on Building</h4>
                  <p>Construct and test your own robotic creations</p>
                </motion.div>

                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaChartLine /></div>
                  <h4>Data Analysis</h4>
                  <p>Learn to collect and analyze data from sensors</p>
                </motion.div>
              </motion.div>

              <p className="track-note">Ideal for students who love gadgets, programming, and automation.</p>

              <div className="track-actions">
                <Link to="/register?track=robotics" className="btn btn-primary track-cta">
                  Register Now
                </Link>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="track-card"
            id="drone"
            ref={track2Ref}
            initial="hidden"
            animate={track2InView ? "visible" : "hidden"}
            variants={fadeIn}
          >
            <div className="track-content">
              <div className="track-header">
                <span className="track-number">Track 2</span>
                <h3 className="track-title">Drone Tech + Aero Modelling</h3>
                <p className="track-tagline">Design, Build, Fly</p>
              </div>

              <div className="track-main-content">
                <div className="track-info">
                  <ul className="track-features">
                    <li>Understanding Science of Aerodynamics and Flight</li>
                    <li>Design, Build and Fly Mama Bug Glider</li>
                    <li>Build and Fly RC Planes and Gliders</li>
                    <li>Understanding Drones and Its Parts</li>
                    <li>Drone Wire Frame, Construction, Frame and Testing</li>
                    <li>Drone Programming and Automation</li>
                    <li>Drones and Real-World Application-Based Projects</li>
                  </ul>
                </div>

                <motion.div 
                  className="track-image-container"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img src="/images/gallery/areromodling.jpg" alt="Students working with drones and aeromodelling" />
                  <div className="image-overlay">
                    <span>Practical Drone Experience</span>
                  </div>
                </motion.div>
              </div>

              <motion.div
                className="track-skills"
                variants={staggerContainer}
              >
                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaMicrochip /></div>
                  <h4>Flight Controllers</h4>
                  <p>Learn about drone flight control systems</p>
                </motion.div>

                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaCode /></div>
                  <h4>Flight Programming</h4>
                  <p>Program autonomous flight patterns</p>
                </motion.div>

                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaPlane /></div>
                  <h4>Model Construction</h4>
                  <p>Build and customize your own flying models</p>
                </motion.div>

                <motion.div className="skill-item" variants={skillVariants}>
                  <div className="skill-icon"><FaChartLine /></div>
                  <h4>Performance Testing</h4>
                  <p>Test and optimize your drone's performance</p>
                </motion.div>
              </motion.div>

              <p className="track-note">Perfect for learners who dream of flying machines and technology.</p>

              <div className="track-actions">
                <Link to="/register?track=drone" className="btn btn-primary track-cta">
                  Register Now
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TracksSection;
